import { i18nNsFactory } from '@atlas-frontend/i18n';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  filterOption,
  FormField,
  FormRadioGroup,
  FormSelect,
} from '../../common/form';
import translations from '../translations/AlternativeAlgorithmForm.translations.json';
import { DynamicFormRow } from './DynamicFormRow';
import { AlgorithmSetOption } from './types';
import { createDefaultAlternative } from './utils';
import { AlgorithmExtendedType } from '../algorithms/schema/types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternative-algorithm-form',
);
addTranslations(translations);

export interface AlternativeAlgorithmFieldsProps {
  algorithms: AlgorithmExtendedType[];
  algorithmIdMap: Map<string, AlgorithmSetOption>;
  count: number;
  index: number;
  options: AlgorithmSetOption[];
  recalculate: () => void;
  onViewAlternative: (index: number) => void;
}

export function AlternativeAlgorithmFields({
  algorithmIdMap,
  count,
  index,
  options,
  recalculate,
  onViewAlternative,
}: AlternativeAlgorithmFieldsProps) {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const selected = useWatch({
    name: `alternativeAlgorithms.${index}.alternativeId`,
  });

  const selectedOption = useMemo(
    () => algorithmIdMap.get(selected),
    [algorithmIdMap, selected],
  );
  const onAddAlternative = useCallback(() => {
    const alternatives = [...getValues('alternativeAlgorithms')];
    setValue(
      'alternativeAlgorithms',
      alternatives.concat(createDefaultAlternative(false)),
      { shouldDirty: true },
    );
  }, [getValues, setValue]);
  const onChange = useCallback(
    (e: RadioChangeEvent) => {
      const checked = e.target.value;
      const rows = Math.max(count, 1);
      for (let i = 0; i < rows; i++) {
        setValue(
          `alternativeAlgorithms.${i}.defaultAlgorithm`,
          i === index ? checked : !checked,
          { shouldDirty: true },
        );
      }
    },
    [count, index, setValue],
  );
  const onRemoveAlternative = useCallback(() => {
    let alternatives = [...getValues('alternativeAlgorithms')];
    if (count < 2) {
      alternatives = [createDefaultAlternative(false)];
    } else {
      alternatives.splice(index, 1);
    }
    setValue('alternativeAlgorithms', alternatives, { shouldDirty: true });
    recalculate();
  }, [count, getValues, recalculate, index, setValue]);

  const allOptions = useMemo(
    () =>
      (selectedOption ? [selectedOption, ...options] : options).toSorted(
        (a, b) => a.label.localeCompare(b.label),
      ),
    [selectedOption, options],
  );

  return (
    <DynamicFormRow
      count={count}
      index={index}
      onAddAlternative={onAddAlternative}
      onRemoveAlternative={onRemoveAlternative}
      onViewAlternative={selected ? onViewAlternative : undefined}
    >
      <FormField
        hiddenLabel={index > 0}
        label={t('algorithm.default.label')}
        name={`alternativeAlgorithms.${index}.defaultAlgorithm`}
        required
      >
        <FormRadioGroup
          name={`alternativeAlgorithms.${index}.defaultAlgorithm`}
          onChange={onChange}
          options={[{ value: true, label: '' }]}
        />
      </FormField>
      <AlgorithmSelectWrapper
        hiddenLabel={index > 0}
        label={t('algorithm.alternativeId.label')}
        name={`alternativeAlgorithms.${index}.alternativeId`}
        required
      >
        <FormSelect
          name={`alternativeAlgorithms.${index}.alternativeId`}
          placeholder={t('algorithm.alternativeId.placeholder')}
          onChange={recalculate}
          options={allOptions}
          filterOption={filterOption}
          showSearch
        />
      </AlgorithmSelectWrapper>
    </DynamicFormRow>
  );
}

const AlgorithmSelectWrapper = styled(FormField)`
  min-width: 315px;
`;
